// VendorDetail.jsx
// Vendor detail page with editable info and products supplied

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Form } from 'react-bootstrap';
import VendorAPI from '../../../services/vendorService';
import InventoryAPI from '../../../services/inventoryService';
import ProductSelector from '../../partial/ProductSelector';
import { formatProductLabel } from '../../../util/productLabel';
import { normalizeWebsite, safeWebsiteHref } from '../../../util/websiteUrl';

const DESKTOP_BREAKPOINT = 1024;

class VendorDetail extends Component {
    state = {
        vendor: null,
        vendorProducts: [],
        // Installer products, used to map a vendor SKU to the specific size
        // variant it supplies (vendor_products.installer_product_id).
        installerProducts: [],
        unitsOfMeasure: [],

        // Edit form
        form: {},
        hasChanges: false,

        // Add product modal
        showAddProductModal: false,
        selectedProduct: null, // from ProductSelector
        addProductForm: {
            installer_product_id: '',
            vendor_sku: '',
            unit_cost: '',
            minimum_order_qty: '',
            order_multiple: '',
            order_unit_id: '',
            unit_quantity: '',
            preferred: false
        },

        // Edit product inline
        editingProductId: null,
        editProductForm: {},

        loading: true,
        saving: false,
        isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT,
        error: null,
        websiteError: null
    };

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadData();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    loadData = async () => {
        const vendorId = this.props.match.params.id;
        try {
            const [vendor, vendorProducts, unitsOfMeasure, installerProducts] = await Promise.all([
                VendorAPI.getVendor(vendorId),
                VendorAPI.getVendorProducts(vendorId),
                InventoryAPI.getUnitsOfMeasure(),
                InventoryAPI.getProducts(this.props.typeId)
            ]);
            const raw = Array.isArray(vendor) ? vendor[0] : vendor;
            const vendorData = { ...raw, vendor_id: raw.vendor_id || raw.id };
            this.setState({
                vendor: vendorData,
                form: { ...vendorData },
                vendorProducts: (Array.isArray(vendorProducts) ? vendorProducts : []).map(vp => ({
                    ...vp,
                    vendor_product_id: vp.vendor_product_id || vp.id
                })),
                unitsOfMeasure: Array.isArray(unitsOfMeasure) ? unitsOfMeasure : [],
                installerProducts: Array.isArray(installerProducts) ? installerProducts : [],
                loading: false
            });
        } catch (err) {
            console.error('Error loading vendor:', err);
            this.setState({ error: 'Failed to load vendor.', loading: false });
        }
    };

    handleFormChange = (field, value) => {
        this.setState(prev => ({
            form: { ...prev.form, [field]: value },
            hasChanges: true
        }));
    };

    handleSaveVendor = async () => {
        const { form, vendor } = this.state;
        let website;
        try {
            website = normalizeWebsite(form.website);
        } catch (e) {
            this.setState({ websiteError: e.message });
            return;
        }
        this.setState({ saving: true, websiteError: null });
        try {
            await VendorAPI.updateVendor(vendor.vendor_id, {
                vendor_name: form.vendor_name,
                contact_name: form.contact_name,
                contact_email: form.contact_email,
                contact_phone: form.contact_phone,
                website,
                lead_time_days: form.lead_time_days ? parseInt(form.lead_time_days, 10) : null,
                notes: form.notes
            });
            // Reflect the normalised value (e.g. "diydetail.com" -> "https://diydetail.com").
            this.setState(prev => ({ saving: false, hasChanges: false, form: { ...prev.form, website: website || '' } }));
        } catch (err) {
            console.error('Error saving vendor:', err);
            const serverMessage = err && err.response && err.response.data && err.response.data.message;
            this.setState({ saving: false, websiteError: serverMessage || null });
        }
    };

    // Product mapping CRUD
    handleAddProduct = async () => {
        const { addProductForm, selectedProduct } = this.state;
        if (!selectedProduct) return;

        this.setState({ saving: true });
        try {
            // Create product first if it's new
            let productId = selectedProduct.product_id;
            if (selectedProduct.isNew || !productId) {
                productId = await this.productSelectorRef.createProductIfNeeded();
            }

            const vendorId = this.props.match.params.id;
            await VendorAPI.addVendorProduct(vendorId, {
                master_product_id: productId,
                installer_product_id: addProductForm.installer_product_id
                    ? parseInt(addProductForm.installer_product_id, 10) : null,
                vendor_sku: addProductForm.vendor_sku || null,
                unit_cost: addProductForm.unit_cost ? parseFloat(addProductForm.unit_cost) : null,
                minimum_order_qty: addProductForm.minimum_order_qty ? parseInt(addProductForm.minimum_order_qty, 10) : null,
                order_multiple: addProductForm.order_multiple ? parseInt(addProductForm.order_multiple, 10) : null,
                order_unit_id: addProductForm.order_unit_id ? parseInt(addProductForm.order_unit_id, 10) : null,
                unit_quantity: addProductForm.unit_quantity ? parseFloat(addProductForm.unit_quantity) : null,
                preferred: addProductForm.preferred
            });
            const vendorProducts = await VendorAPI.getVendorProducts(vendorId);
            this.setState({
                vendorProducts: (Array.isArray(vendorProducts) ? vendorProducts : []).map(vp => ({ ...vp, vendor_product_id: vp.vendor_product_id || vp.id })),
                showAddProductModal: false,
                selectedProduct: null,
                addProductForm: { installer_product_id: '', vendor_sku: '', unit_cost: '', minimum_order_qty: '', order_multiple: '', order_unit_id: '', unit_quantity: '', preferred: false },
                saving: false
            });
        } catch (err) {
            console.error('Error adding product:', err);
            alert(err.message || 'Failed to add product.');
            this.setState({ saving: false });
        }
    };

    handleStartEditProduct = (vp) => {
        this.setState({
            editingProductId: vp.vendor_product_id,
            editProductForm: {
                installer_product_id: vp.installer_product_id || '',
                vendor_sku: vp.vendor_sku || '',
                unit_cost: vp.unit_cost || '',
                minimum_order_qty: vp.minimum_order_qty || '',
                order_multiple: vp.order_multiple || '',
                order_unit_id: vp.order_unit_id || '',
                unit_quantity: vp.unit_quantity || '',
                preferred: vp.preferred || false
            }
        });
    };

    handleSaveEditProduct = async () => {
        const { editingProductId, editProductForm } = this.state;
        if (!editingProductId) {
            console.error('No vendor product ID for update');
            return;
        }
        this.setState({ saving: true });
        try {
            await VendorAPI.updateVendorProduct(editingProductId, {
                installer_product_id: editProductForm.installer_product_id
                    ? parseInt(editProductForm.installer_product_id, 10) : null,
                vendor_sku: editProductForm.vendor_sku || null,
                unit_cost: editProductForm.unit_cost ? parseFloat(editProductForm.unit_cost) : null,
                minimum_order_qty: editProductForm.minimum_order_qty ? parseInt(editProductForm.minimum_order_qty, 10) : null,
                order_multiple: editProductForm.order_multiple ? parseInt(editProductForm.order_multiple, 10) : null,
                order_unit_id: editProductForm.order_unit_id ? parseInt(editProductForm.order_unit_id, 10) : null,
                unit_quantity: editProductForm.unit_quantity ? parseFloat(editProductForm.unit_quantity) : null,
                preferred: editProductForm.preferred
            });
            const vendorId = this.props.match.params.id;
            const vendorProducts = await VendorAPI.getVendorProducts(vendorId);
            this.setState({
                vendorProducts: (Array.isArray(vendorProducts) ? vendorProducts : []).map(vp => ({ ...vp, vendor_product_id: vp.vendor_product_id || vp.id })),
                editingProductId: null,
                saving: false
            });
        } catch (err) {
            console.error('Error updating product mapping:', err);
            this.setState({ saving: false });
        }
    };

    handleDeleteProduct = async (vendorProductId) => {
        if (!window.confirm('Remove this product from this vendor?')) return;
        try {
            await VendorAPI.deleteVendorProduct(vendorProductId);
            const vendorId = this.props.match.params.id;
            const vendorProducts = await VendorAPI.getVendorProducts(vendorId);
            this.setState({ vendorProducts: (Array.isArray(vendorProducts) ? vendorProducts : []).map(vp => ({ ...vp, vendor_product_id: vp.vendor_product_id || vp.id })) });
        } catch (err) {
            console.error('Error removing product:', err);
        }
    };

    handleProductSelectorChange = (selection) => {
        this.setState({ selectedProduct: selection });
    };

    render() {
        const { form, vendorProducts, unitsOfMeasure, installerProducts, loading, saving, hasChanges, error, isDesktop,
                showAddProductModal, addProductForm, selectedProduct, editingProductId, editProductForm } = this.state;

        const sortedUnits = [...unitsOfMeasure].sort((a, b) =>
            (a.unit_name || a.name || '').localeCompare(b.unit_name || b.name || ''));

        const getUnitName = (unitId) => {
            if (!unitId) return '—';
            const unit = unitsOfMeasure.find(u => String(u.unit_id || u.id) === String(unitId));
            return unit ? (unit.abbreviation || unit.unit_name || unit.name) : '—';
        };

        if (loading) {
            return (
                <div className="text-center py-5">
                    <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-3">Loading vendor...</p>
                </div>
            );
        }

        if (error) {
            return <div className="alert alert-danger m-3">{error}</div>;
        }

        return (
            <div className={`vendor-detail-container ${!isDesktop ? 'mobile' : ''}`}>
                <style>{`
                    .vendor-detail-container {
                        max-width: 1400px;
                        margin: 0 auto;
                        padding: var(--space-6);
                        background: var(--color-bg-panel);
                        min-height: 100vh;
                    }
                    .vendor-detail-container.mobile { padding: var(--space-4); }
                    .vendor-form-row {
                        display: flex;
                        gap: var(--space-4);
                        flex-wrap: wrap;
                    }
                    .vendor-form-row .form-group { flex: 1; min-width: 200px; }
                    .preferred-star { color: var(--color-warning); cursor: pointer; }
                    .preferred-star-empty { color: var(--color-gray-300); cursor: pointer; }
                    .jv-inline-input {
                        padding: var(--space-1) var(--space-2);
                        border: 1px solid var(--color-border);
                        border-radius: var(--radius-sm);
                        font-size: var(--font-size-sm);
                        width: 100%;
                    }
                    .jv-inline-input:focus { outline: none; border-color: var(--color-brand-teal); }
                `}</style>

                {/* Tab Toggle */}
                <div className="text-center mb-3">
                    <div className="btn-group" role="group">
                        <Link
                            to="/purchasing"
                            className="btn btn-outline-primary"
                            style={{ textDecoration: 'none' }}>
                            Purchasing
                        </Link>
                        <Link
                            to="/purchasing?tab=vendors"
                            className="btn btn-primary"
                            style={{ textDecoration: 'none' }}>
                            Vendors
                        </Link>
                    </div>
                </div>

                <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-base)' }}>
                    <Link to="/purchasing?tab=vendors" style={{ color: 'var(--color-brand-teal)', textDecoration: 'none' }}>
                        <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>Return to Vendor List
                    </Link>
                </div>

                {/* Vendor Info */}
                <div className="jv-card">
                    <h5 className="jv-card-title"><i className="fa-sharp fa-regular fa-truck-field mr-2"></i>Vendor Information</h5>

                    <div className="vendor-form-row">
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={form.vendor_name || ''}
                                onChange={(e) => this.handleFormChange('vendor_name', e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Contact Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.contact_name || ''}
                                onChange={(e) => this.handleFormChange('contact_name', e.target.value)}
                            />
                        </Form.Group>
                    </div>

                    <div className="vendor-form-row">
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={form.contact_email || ''}
                                onChange={(e) => this.handleFormChange('contact_email', e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.contact_phone || ''}
                                onChange={(e) => this.handleFormChange('contact_phone', e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <div className="d-flex align-items-center" style={{ gap: 'var(--space-2)' }}>
                                <Form.Control
                                    type="text"
                                    inputMode="url"
                                    maxLength={500}
                                    placeholder="e.g. diydetail.com"
                                    isInvalid={!!this.state.websiteError}
                                    value={form.website || ''}
                                    onChange={(e) => { this.setState({ websiteError: null }); this.handleFormChange('website', e.target.value); }}
                                />
                                {safeWebsiteHref(form.website) && (
                                    <a href={safeWebsiteHref(form.website)} target="_blank" rel="noopener noreferrer"
                                        className="text-nowrap" title="Open the vendor's website in a new tab">
                                        Visit <i className="fa-sharp fa-regular fa-arrow-up-right-from-square ml-1"></i>
                                    </a>
                                )}
                            </div>
                            {this.state.websiteError && (
                                <Form.Text className="text-danger">{this.state.websiteError}</Form.Text>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Lead Time (days)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={form.lead_time_days || ''}
                                onChange={(e) => this.handleFormChange('lead_time_days', e.target.value)}
                            />
                        </Form.Group>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={form.notes || ''}
                            onChange={(e) => this.handleFormChange('notes', e.target.value)}
                        />
                    </Form.Group>

                    {hasChanges && (
                        <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={this.handleSaveVendor} disabled={saving}>
                            {saving ? (
                                <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Saving...</>
                            ) : (
                                <><i className="fa-sharp fa-regular fa-floppy-disk mr-2"></i>Save Changes</>
                            )}
                        </button>
                    )}
                </div>

                {/* Products Supplied */}
                <div className="jv-card">
                    <div className="jv-card-header">
                        <h5 className="jv-card-title"><i className="fa-sharp fa-regular fa-cubes mr-2"></i>Products Supplied</h5>
                        <button className="jv-btn jv-btn-primary jv-btn-sm" onClick={() => this.setState({ showAddProductModal: true })}>
                            <i className="fa-sharp fa-regular fa-plus mr-2"></i>Add Product
                        </button>
                    </div>

                    {vendorProducts.length === 0 ? (
                        <div className="jv-empty-state">
                            <i className="fa-sharp fa-regular fa-cubes"></i>
                            <p>No products mapped. Add products this vendor supplies.</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="jv-table jv-table-sm">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Brand</th>
                                        <th>Vendor SKU</th>
                                        <th>Unit Cost</th>
                                        <th>Order Unit</th>
                                        <th>Unit Qty</th>
                                        <th>MOQ</th>
                                        {isDesktop && <th>Order Multiple</th>}
                                        <th>Preferred</th>
                                        <th style={{ width: '100px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendorProducts.map(vp => {
                                        const isEditing = editingProductId === vp.vendor_product_id;
                                        return (
                                            <tr key={vp.vendor_product_id}>
                                                <td>
                                                    {vp.product_name || vp.name || '—'}
                                                    {isEditing ? (() => {
                                                        // Which size this vendor SKU supplies. Only offered when the
                                                        // master actually has installer products to choose between.
                                                        const variants = (installerProducts || []).filter(p =>
                                                            String(p.master_product_id) === String(vp.master_product_id));
                                                        if (variants.length === 0) return null;
                                                        return (
                                                            <select className="jv-inline-input mt-1" style={{ width: '100%' }}
                                                                value={editProductForm.installer_product_id}
                                                                onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, installer_product_id: e.target.value } }))}>
                                                                <option value="">Size not specified</option>
                                                                {variants.map(p => (
                                                                    <option key={p.product_id || p.id} value={String(p.product_id || p.id)}>
                                                                        supplies {formatProductLabel(p)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        );
                                                    })() : (vp.installer_product_variant
                                                        ? <small className="text-muted d-block">supplies {vp.installer_product_variant}</small>
                                                        : null)}
                                                </td>
                                                <td>{vp.brand || '—'}</td>
                                                <td>
                                                    {isEditing ? (
                                                        <input className="jv-inline-input" value={editProductForm.vendor_sku}
                                                            onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, vendor_sku: e.target.value } }))} />
                                                    ) : (vp.vendor_sku || '—')}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <input className="jv-inline-input" type="number" step="0.01" value={editProductForm.unit_cost}
                                                            onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, unit_cost: e.target.value } }))} />
                                                    ) : (vp.unit_cost ? `$${parseFloat(vp.unit_cost).toFixed(2)}` : '—')}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <select className="jv-inline-input" value={editProductForm.order_unit_id}
                                                            onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, order_unit_id: e.target.value } }))}>
                                                            <option value="">—</option>
                                                            {sortedUnits.map(u => (
                                                                <option key={u.unit_id || u.id} value={u.unit_id || u.id}>
                                                                    {u.unit_name || u.name}{u.abbreviation ? ` (${u.abbreviation})` : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : getUnitName(vp.order_unit_id)}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <input className="jv-inline-input" type="number" step="0.01" min="0" value={editProductForm.unit_quantity}
                                                            onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, unit_quantity: e.target.value } }))} />
                                                    ) : (vp.unit_quantity ? `${vp.unit_quantity} ${vp.base_unit_code || vp.default_unit_code || ''}`.trim() : '—')}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <input className="jv-inline-input" type="number" min="0" value={editProductForm.minimum_order_qty}
                                                            onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, minimum_order_qty: e.target.value } }))} />
                                                    ) : (vp.minimum_order_qty || '—')}
                                                </td>
                                                {isDesktop && (
                                                    <td>
                                                        {isEditing ? (
                                                            <input className="jv-inline-input" type="number" min="0" value={editProductForm.order_multiple}
                                                                onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, order_multiple: e.target.value } }))} />
                                                        ) : (vp.order_multiple || '—')}
                                                    </td>
                                                )}
                                                <td>
                                                    {isEditing ? (
                                                        <input type="checkbox" checked={editProductForm.preferred}
                                                            onChange={(e) => this.setState(prev => ({ editProductForm: { ...prev.editProductForm, preferred: e.target.checked } }))} />
                                                    ) : (
                                                        <i className={vp.preferred ? 'fa-sharp fa-solid fa-star preferred-star' : 'fa-sharp fa-regular fa-star preferred-star-empty'}></i>
                                                    )}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <>
                                                            <button className="jv-btn-icon" onClick={this.handleSaveEditProduct} disabled={saving} title="Save">
                                                                {saving ? <i className="fa-sharp fa-regular fa-spinner fa-spin"></i> : <i className="fa-sharp fa-regular fa-check"></i>}
                                                            </button>
                                                            <button className="jv-btn-icon" onClick={() => this.setState({ editingProductId: null })} title="Cancel">
                                                                <i className="fa-sharp fa-regular fa-xmark"></i>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="jv-btn-icon" onClick={() => this.handleStartEditProduct(vp)} title="Edit">
                                                                <i className="fa-sharp fa-regular fa-pen-to-square"></i>
                                                            </button>
                                                            <button className="jv-btn-icon" onClick={() => this.handleDeleteProduct(vp.vendor_product_id)} title="Remove" style={{ color: 'var(--color-danger)' }}>
                                                                <i className="fa-sharp fa-regular fa-trash"></i>
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Add Product Modal */}
                <Modal show={showAddProductModal} onHide={() => this.setState({ showAddProductModal: false, selectedProduct: null })} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fa-sharp fa-regular fa-cubes mr-2"></i>
                            Add Vendor Product
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ProductSelector
                            ref={(ref) => { this.productSelectorRef = ref; }}
                            installerId={this.props.typeId}
                            useMasterCatalog={true}
                            onChange={this.handleProductSelectorChange}
                        />

                        {selectedProduct && selectedProduct.product_name && (() => {
                            const baseUnitId = selectedProduct.default_unit_id ||
                                (selectedProduct.product && (selectedProduct.product.unit_id || selectedProduct.product.default_unit_id));
                            const baseUnit = baseUnitId
                                ? unitsOfMeasure.find(u => String(u.unit_id || u.id) === String(baseUnitId))
                                : null;
                            const baseUnitName = baseUnit ? (baseUnit.unit_name || baseUnit.name) : (selectedProduct.default_unit_code || null);
                            return (
                            <>
                                <hr />
                                <h6 className="mb-3">Vendor Details for {formatProductLabel(selectedProduct)}</h6>
                                {baseUnitName && (
                                    <div className="mb-3" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                        <i className="fa-sharp fa-regular fa-ruler mr-1"></i>
                                        Inventory base unit: <strong style={{ color: 'var(--color-text-primary)' }}>{baseUnitName}</strong>
                                    </div>
                                )}

                                {(() => {
                                    // A vendor SKU supplies ONE size. The master product covers every
                                    // variant, so record which one this SKU is — that is what lets
                                    // receiving book stock against the right product instead of
                                    // refusing to guess between them.
                                    const variants = (installerProducts || []).filter(p =>
                                        String(p.master_product_id) === String(selectedProduct.product_id));
                                    if (variants.length === 0) return null;
                                    return (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Which size does this SKU supply?</Form.Label>
                                            <Form.Control as="select"
                                                value={addProductForm.installer_product_id}
                                                onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, installer_product_id: e.target.value } }))}>
                                                <option value="">Not specified</option>
                                                {variants.map(p => (
                                                    <option key={p.product_id || p.id} value={String(p.product_id || p.id)}>
                                                        {formatProductLabel(p)}{p.sku ? ` — ${p.sku}` : ''}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                            <Form.Text className="text-muted">
                                                {variants.length > 1
                                                    ? 'This product has more than one size. Leave unspecified and receiving cannot tell them apart.'
                                                    : 'Optional while this product has a single size.'}
                                            </Form.Text>
                                        </Form.Group>
                                    );
                                })()}

                                <Form.Group className="mb-3">
                                    <Form.Label>Vendor SKU</Form.Label>
                                    <Form.Control type="text"
                                        value={addProductForm.vendor_sku}
                                        onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, vendor_sku: e.target.value } }))} />
                                </Form.Group>

                                <div className="d-flex" style={{ gap: 'var(--space-3)' }}>
                                    <Form.Group className="mb-3 flex-fill">
                                        <Form.Label>Order Unit</Form.Label>
                                        <Form.Control as="select"
                                            value={addProductForm.order_unit_id}
                                            onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, order_unit_id: e.target.value } }))}>
                                            <option value="">Select unit...</option>
                                            {sortedUnits.map(u => (
                                                <option key={u.unit_id || u.id} value={u.unit_id || u.id}>
                                                    {u.unit_name || u.name}{u.abbreviation ? ` (${u.abbreviation})` : ''}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mb-3 flex-fill">
                                        <Form.Label>Unit Qty{baseUnitName ? ` (${baseUnitName} per order unit)` : ''}</Form.Label>
                                        <Form.Control type="number" step="0.01" min="0"
                                            value={addProductForm.unit_quantity}
                                            onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, unit_quantity: e.target.value } }))} />
                                    </Form.Group>
                                </div>

                                <div className="d-flex" style={{ gap: 'var(--space-3)' }}>
                                    <Form.Group className="mb-3 flex-fill">
                                        <Form.Label>Unit Cost</Form.Label>
                                        <Form.Control type="number" step="0.01" min="0"
                                            value={addProductForm.unit_cost}
                                            onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, unit_cost: e.target.value } }))} />
                                    </Form.Group>
                                    <Form.Group className="mb-3 flex-fill">
                                        <Form.Label>Min Order Qty</Form.Label>
                                        <Form.Control type="number" min="0"
                                            value={addProductForm.minimum_order_qty}
                                            onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, minimum_order_qty: e.target.value } }))} />
                                    </Form.Group>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Preferred vendor for this product"
                                        checked={addProductForm.preferred}
                                        onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, preferred: e.target.checked } }))}
                                    />
                                </Form.Group>
                            </>
                            );
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="jv-btn jv-btn-tertiary" onClick={() => this.setState({ showAddProductModal: false, selectedProduct: null })}>Cancel</button>
                        <button
                            className="jv-btn jv-btn-primary"
                            onClick={this.handleAddProduct}
                            disabled={saving || !selectedProduct || !selectedProduct.product_name}
                        >
                            {saving ? <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Adding...</> : 'Add Product'}
                        </button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export default VendorDetail;
